import React, { useEffect, useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/admin/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/typed";

export function EditExpiration({ code, onClose, onDone }: { code: any; onClose: () => void; onDone: () => void }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (code) { setDate((code.expires_at || "").slice(0, 10)); setNote(""); } }, [code]);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/codes/${code.id}/expiration`, { expires_on: date, note }); toast.success(`Code ${code.code} now expires ${date}`); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="code-expiration-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Change expiration for {code?.code}</DialogTitle></DialogHeader>
        <label className="block"><span className="field-label">New expiration date</span>
          <input type="date" className="field-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="code-expiration-date" /></label>
        <label className="block"><span className="field-label">Reason (optional)</span>
          <input className="field-input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="code-expiration-note" /></label>
        <p className="text-xs text-[#64748B]">A code that expired on its date becomes usable again if the new date is in the future.</p>
        <button className="btn-primary w-full" disabled={busy || !date} onClick={save} data-testid="code-expiration-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save expiration</button>
      </DialogContent>
    </Dialog>
  );
}

export function BulkExpiration({ onDone }: { onDone: () => void }) {
  const [before, setBefore] = useState("2026-03-10");
  const [expires, setExpires] = useState("2028-03-10");
  const [note, setNote] = useState("Codes purchased before Mar 10, 2026 expire Mar 10, 2028 (approved by Kelly, Mar 11, 2026)");
  const [preview, setPreview] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const run = async (dry: boolean) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/codes/bulk-expiration", { scope: "dow", issued_before: before, expires_on: expires, note, dry_run: dry });
      setPreview(dry ? data : null);
      if (!dry) { toast.success(`${data.updated} code${data.updated === 1 ? "" : "s"} now expire ${expires}`); onDone(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Card title="Diocese code expiration" testId="bulk-expiration-card">
      <div className="space-y-3 p-6">
        <p className="text-sm text-[#475569]">Move the expiration date on every unused or in-use code for all Diocese of Wilmington charters issued before a date. Dates are only ever extended, never shortened.</p>
        <label className="block"><span className="field-label">Codes issued before</span>
          <input type="date" className="field-input" value={before} onChange={(e) => { setBefore(e.target.value); setPreview(null); }} data-testid="bulk-expiration-before" /></label>
        <label className="block"><span className="field-label">New expiration date</span>
          <input type="date" className="field-input" value={expires} onChange={(e) => { setExpires(e.target.value); setPreview(null); }} data-testid="bulk-expiration-date" /></label>
        <label className="block"><span className="field-label">Reason</span>
          <input className="field-input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} data-testid="bulk-expiration-note" /></label>
        {!preview ? (
          <button className="btn-secondary btn-sm" disabled={busy || !before || !expires} onClick={() => run(true)} data-testid="bulk-expiration-preview">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Preview</button>
        ) : (
          <div className="space-y-2 rounded-xl bg-[#F1EFEA] p-3 text-sm" data-testid="bulk-expiration-result">
            <p><b>{preview.matched.toLocaleString()}</b> code{preview.matched === 1 ? "" : "s"} will change to expire {expires}{preview.revived ? `, including ${preview.revived} already expired that become usable again` : ""}.</p>
            <button className="btn-primary btn-sm" disabled={busy || !preview.matched} onClick={() => run(false)} data-testid="bulk-expiration-apply">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Update {preview.matched.toLocaleString()} codes</button>
          </div>
        )}
      </div>
    </Card>
  );
}
